import { useEffect, useRef, useState } from 'react';
import { Loader, Plus, Search, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

// The connect form's searches for a mail source: a multi-select combobox that starts
// with the default searches. Each chosen search is a hidden `name` input, so the form
// reads them with FormData.getAll.
interface DefaultQuery {
    text: string;
    section_id: string | null;
}

const same = (a: string, b: string) => a.trim().toLowerCase() === b.trim().toLowerCase();

export default function SearchesField({ name, label, helperText }: { name: string; label: string; helperText?: string }) {
    const { authFetch } = useAuth();
    const [defaults, setDefaults] = useState<DefaultQuery[] | null>(null);
    const [chosen, setChosen] = useState<string[]>([]);
    const [text, setText] = useState('');
    const [open, setOpen] = useState(false);
    const [active, setActive] = useState(0);
    const boxRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        authFetch('/sources/default-queries')
            .then(res => res.ok ? res.json() : [])
            .catch(() => [])
            .then((data: DefaultQuery[]) => {
                setDefaults(data);
                setChosen(data.map(d => d.text));
            });
    }, [authFetch]);

    useEffect(() => {
        const close = (e: MouseEvent) => {
            if (!boxRef.current?.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener('mousedown', close);
        return () => document.removeEventListener('mousedown', close);
    }, []);

    const typed = text.trim();
    const options = (defaults ?? [])
        .map(d => d.text)
        .filter(t => !chosen.some(c => same(c, t)) && t.toLowerCase().includes(typed.toLowerCase()));
    const canAdd = !!typed && !chosen.some(c => same(c, typed)) && !options.some(o => same(o, typed));
    const choices = canAdd ? [...options, typed] : options;

    const add = (value: string) => {
        const v = value.trim().replace(/\s+/g, ' ');
        if (v && !chosen.some(c => same(c, v))) setChosen(prev => [...prev, v]);
        setText('');
        setActive(0);
    };
    const remove = (value: string) => setChosen(prev => prev.filter(c => c !== value));

    const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            if (choices.length) add(choices[Math.min(active, choices.length - 1)]);
        } else if (e.key === 'ArrowDown') {
            e.preventDefault();
            setOpen(true);
            setActive(i => Math.min(i + 1, choices.length - 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActive(i => Math.max(i - 1, 0));
        } else if (e.key === 'Escape') {
            setOpen(false);
        } else if (e.key === 'Backspace' && !text && chosen.length) {
            remove(chosen[chosen.length - 1]);
        }
    };

    return (
        <div>
            <label htmlFor={`${name}-input`} className="block text-sm font-medium text-graphite mb-2">{label}</label>
            {chosen.map(c => <input key={c} type="hidden" name={name} value={c} />)}
            <div ref={boxRef} className="relative">
                <div
                    className="flex flex-wrap items-center gap-1.5 min-h-[42px] px-2 py-1.5 border border-softgrey rounded-lg bg-white focus-within:ring-2 focus-within:ring-brand cursor-text"
                    onClick={() => inputRef.current?.focus()}
                >
                    {defaults === null && <Loader className="w-4 h-4 animate-spin text-coolslate mx-1" />}
                    {chosen.map(c => (
                        <span key={c} className="inline-flex items-center gap-1 max-w-full pl-2.5 pr-1 py-0.5 rounded-md bg-offwhite border border-softgrey text-sm text-graphite">
                            <span className="truncate">{c}</span>
                            <button
                                type="button"
                                onClick={() => remove(c)}
                                aria-label={`Remove ${c}`}
                                className="p-0.5 text-coolslate hover:text-graphite cursor-pointer"
                            >
                                <X className="w-3.5 h-3.5" />
                            </button>
                        </span>
                    ))}
                    <input
                        ref={inputRef}
                        id={`${name}-input`}
                        value={text}
                        onChange={e => { setText(e.target.value); setOpen(true); setActive(0); }}
                        onFocus={() => setOpen(true)}
                        onKeyDown={onKeyDown}
                        placeholder={chosen.length ? 'Add a search' : 'Search for, such as a customer\'s name'}
                        autoComplete="off"
                        className="flex-1 min-w-[10rem] px-1 py-1 text-sm text-graphite bg-transparent focus:outline-none placeholder:text-coolslate"
                        role="combobox"
                        aria-expanded={open && choices.length > 0}
                        aria-controls={`${name}-options`}
                    />
                </div>
                {open && choices.length > 0 && (
                    <ul
                        id={`${name}-options`}
                        role="listbox"
                        className="absolute z-20 mt-1 w-full max-h-56 overflow-y-auto bg-white border border-softgrey rounded-lg shadow-lg py-1"
                    >
                        {choices.map((c, i) => {
                            const isNew = canAdd && i === choices.length - 1;
                            return (
                                <li
                                    key={`${isNew ? 'new' : 'default'}-${c}`}
                                    role="option"
                                    aria-selected={i === active}
                                    onMouseDown={e => { e.preventDefault(); add(c); }}
                                    onMouseEnter={() => setActive(i)}
                                    className={`flex items-center gap-2 px-3 py-2 text-sm cursor-pointer ${i === active ? 'bg-offwhite' : ''}`}
                                >
                                    {isNew
                                        ? <><Plus className="w-3.5 h-3.5 text-brand-dark" />Add "{c}"</>
                                        : <><Search className="w-3.5 h-3.5 text-coolslate" />{c}<span className="ml-auto text-xs text-coolslate">Default</span></>}
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>
            {helperText && <p className="text-xs text-coolslate mt-1">{helperText}</p>}
        </div>
    );
}
